/**
 * 派系配置编辑器组件
 * 提供派系配置的表单式编辑界面
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Typography,
  Table,
  Switch,
  message,
  Divider
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  FlagOutlined,
  TeamOutlined,
  ThunderboltOutlined
} from '@ant-design/icons'
import type { FactionConfigData, FactionRelationship } from '@/types/scriptConfigBlock'

const { TextArea } = Input
const { Text, Title } = Typography

// 派系类型选项
const FACTION_TYPE_OPTIONS = [
  { value: 'intelligence', label: '情报' },
  { value: 'combat', label: '战斗' },
  { value: 'social', label: '社交' },
  { value: 'wealth', label: '财富' },
  { value: 'medical', label: '医疗' },
  { value: 'logistics', label: '后勤' },
  { value: 'ally', label: '友军' },
  { value: 'enemy', label: '敌军' },
  { value: 'neutral', label: '中立' },
]

// 关系类型
const RELATIONSHIP_TYPE_OPTIONS = [
  { value: 'ally', label: '同盟', color: 'green' },
  { value: 'neutral', label: '中立', color: 'default' },
  { value: 'hostile', label: '敌对', color: 'red' },
]

// 派系编辑器属性
interface FactionEditorFormProps {
  initialData?: FactionConfigData
  onSave: (data: FactionConfigData) => Promise<void>
  loading?: boolean
}

// 关系编辑器
const RelationshipEditor: React.FC<{
  relationship: FactionRelationship
  index: number
  onUpdate: (index: number, rel: FactionRelationship) => void
  onRemove: (index: number) => void
}> = ({ relationship, index, onUpdate, onRemove }) => {
  return (
    <Row gutter={8} style={{ marginBottom: 8 }}>
      <Col span={8}>
        <Input
          value={relationship.target_faction_key}
          onChange={(e) => onUpdate(index, { ...relationship, target_faction_key: e.target.value })}
          placeholder="目标派系键"
        />
      </Col>
      <Col span={6}>
        <InputNumber
          value={relationship.initial_value}
          onChange={(value) => onUpdate(index, { ...relationship, initial_value: value || 0 })}
          min={-100}
          max={100}
          style={{ width: '100%' }}
          placeholder="初始值"
        />
      </Col>
      <Col span={8}>
        <Input
          value={relationship.description || ''}
          onChange={(e) => onUpdate(index, { ...relationship, description: e.target.value })}
          placeholder="关系描述"
        />
      </Col>
      <Col span={2}>
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
      </Col>
    </Row>
  )
}

// 主组件
const FactionEditorForm: React.FC<FactionEditorFormProps> = ({
  initialData,
  onSave,
  loading = false
}) => {
  const [form] = Form.useForm()
  const [relationships, setRelationships] = useState<FactionRelationship[]>([])
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      // 处理 goals 数组：确保元素是字符串再 join
      const normalizeGoals = (goals: any): string => {
        if (!Array.isArray(goals)) return ''
        return goals.map(g => String(g)).join('\n')
      }

      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description,
        ideology: initialData.ideology,
        goals: normalizeGoals(initialData.goals),
        // 展开嵌套的 base_attributes 对象
        base_attributes: {
          strength: initialData.base_attributes?.strength,
          intelligence: initialData.base_attributes?.intelligence,
          social: initialData.base_attributes?.social,
          health: initialData.base_attributes?.health,
          gold: initialData.base_attributes?.gold,
          wealth: initialData.base_attributes?.wealth,
        },
        // 奖励字段
        join_reward_gold: initialData.join_reward_gold,
        join_reward_item: initialData.join_reward_item,
      })
      setRelationships(initialData.relationships || [])
    } else {
      form.resetFields()
      setRelationships([])
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const factionData: FactionConfigData = {
        key: values.key,
        name: values.name,
        description: values.description,
        ideology: values.ideology,
        goals: values.goals ? values.goals.split('\n').filter((g: string) => g.trim()) : [],
        relationships,
        base_attributes: {
          strength: values.base_attributes?.strength || 0,
          intelligence: values.base_attributes?.intelligence || 0,
          social: values.base_attributes?.social || 0,
          health: values.base_attributes?.health || 0,
          gold: values.base_attributes?.gold || 0,
          wealth: values.base_attributes?.wealth || 0,
        },
        join_reward_gold: values.join_reward_gold,
        join_reward_item: values.join_reward_item,
      }

      await onSave(factionData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  // 关系操作
  const addRelationship = () => {
    setRelationships([
      ...relationships,
      { target_faction_key: '', initial_value: 0 }
    ])
  }

  const updateRelationship = (index: number, rel: FactionRelationship) => {
    const newRels = [...relationships]
    newRels[index] = rel
    setRelationships(newRels)
  }

  const removeRelationship = (index: number) => {
    setRelationships(relationships.filter((_, i) => i !== index))
  }

  return (
    <Card
      title={
        <Space>
          <FlagOutlined style={{ color: '#722ed1' }} />
          <span>{initialData?.name || '新建派系'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<TeamOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="派系键" rules={[{ required: true }]}>
                  <Input placeholder="例如: hongmen" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="派系名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 洪门帮派" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="ideology" label="派系理念">
                  <Input placeholder="例如: 帮派利益" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <TextArea rows={3} placeholder="派系的详细描述..." />
            </Form.Item>

            <Form.Item name="goals" label="目标 (每行一个)">
              <TextArea rows={3} placeholder="派系目标，每行一个..." />
            </Form.Item>
          </TabPane>

          <TabPane tab={`关系 (${relationships.length})`} key="relationships">
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={addRelationship}>
                添加关系
              </Button>
            </div>

            <Row gutter={8} style={{ marginBottom: 8 }}>
              <Col span={8}><Text strong>目标派系</Text></Col>
              <Col span={6}><Text strong>初始值 (-100~100)</Text></Col>
              <Col span={8}><Text strong>描述</Text></Col>
              <Col span={2}></Col>
            </Row>

            {relationships.map((rel, index) => (
              <RelationshipEditor
                key={index}
                relationship={rel}
                index={index}
                onUpdate={updateRelationship}
                onRemove={removeRelationship}
              />
            ))}

            {relationships.length === 0 && (
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text type="secondary">暂无关系配置，点击上方按钮添加</Text>
              </Card>
            )}

            <Divider>关系说明</Divider>
            <Card size="small">
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                <li><Text code>100</Text> - 完美同盟</li>
                <li><Text code>50</Text> - 友好关系</li>
                <li><Text code>0</Text> - 中立</li>
                <li><Text code>-50</Text> - 敌对关系</li>
                <li><Text code>-100</Text> - 死敌</li>
              </ul>
            </Card>
          </TabPane>

          <TabPane tab="基础属性" key="attributes">
            <Alert
              message="基础属性说明"
              description="派系成员入会时获得的属性增益。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name={['base_attributes', 'strength']} label="力量增益">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={['base_attributes', 'intelligence']} label="智力增益">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={['base_attributes', 'social']} label="社交增益">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={['base_attributes', 'health']} label="生命增益">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name={['base_attributes', 'gold']} label="初始金币">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={['base_attributes', 'wealth']} label="财富增益">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="成员奖励" key="rewards">
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text>配置派系成员可获得的奖励，包括金币、物品、能力等。</Text>
            </Card>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="join_reward_gold" label="加入奖励金币">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="join_reward_item" label="加入奖励物品">
                  <Input placeholder="物品键，多个用逗号分隔" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs
const { Alert } = Card

export default FactionEditorForm
